import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Spinner } from '../components/ui/spinner';
import { Globe, Eye, EyeOff, Mail, Phone } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const [loginMode, setLoginMode] = useState('email'); // 'email' | 'phone'
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(identifier, password);
      // Redirect based on user role
      if (userData.role === 'super_admin') {
        navigate('/motopotti-admin');
      } else if (userData.tenant_slug) {
        navigate(`/${userData.tenant_slug}/admin`);
      } else {
        navigate('/admin');
      }
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Login failed. Please try again.' : 'লগইন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: '#041408' }}>
      {/* Top forest wash */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[55%]" style={{ background: 'radial-gradient(ellipse at 50% -20%, #0E461C 0%, #013C10 35%, transparent 75%)' }} />
      {/* Single tiny brand-green spark, low opacity */}
      <div className="pointer-events-none absolute -top-24 left-1/3 w-[320px] h-[320px] rounded-full blur-3xl" style={{ background: 'rgba(25,218,98,0.12)' }} />
      {/* Hairline brand accent at very top */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #19DA62, transparent)' }} />
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.06]">
        <div className="absolute inset-0" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
      </div>

      <div className="w-full max-w-md relative">
        {/* Language Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          className="absolute -top-12 right-0 text-white/80 hover:text-white hover:bg-white/10"
          data-testid="login-language-toggle"
        >
          <Globe className="w-4 h-4 mr-2" />
          {language === 'en' ? 'বাংলা' : 'English'}
        </Button>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            {/* Brand Logo + Wordmark */}
            <div className="flex flex-col items-center gap-3 mb-4">
              <div className="flex items-center gap-2.5">
                <img src="/motopotti-logo.png" alt="" aria-hidden="true" className="h-12 w-auto" />
                <img src="/motopotti-wordmark.png" alt="MotoPotti" className="h-10 w-auto" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold font-['Barlow_Condensed']" style={{ color: '#013C10' }}>
              {t('login_title')}
            </CardTitle>
            <CardDescription>
              {t('login_subtitle')}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              {/* Email / Phone toggle */}
              <div className="flex rounded-lg bg-slate-100 p-1">
                <button type="button" onClick={() => { setLoginMode('email'); setIdentifier(''); }}
                  style={loginMode === 'email' ? { color: '#013C10' } : undefined}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-md transition-colors ${loginMode === 'email' ? 'bg-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>
                  <Mail className="w-4 h-4" />{language === 'en' ? 'Email' : 'ইমেইল'}
                </button>
                <button type="button" onClick={() => { setLoginMode('phone'); setIdentifier(''); }}
                  style={loginMode === 'phone' ? { color: '#013C10' } : undefined}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-md transition-colors ${loginMode === 'phone' ? 'bg-white shadow' : 'text-slate-500 hover:text-slate-700'}`}>
                  <Phone className="w-4 h-4" />{language === 'en' ? 'Phone' : 'ফোন'}
                </button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="identifier">{loginMode === 'email' ? t('email') : (language === 'en' ? 'Phone Number' : 'ফোন নম্বর')}</Label>
                <Input
                  id="identifier"
                  type={loginMode === 'email' ? 'email' : 'tel'}
                  placeholder={loginMode === 'email' ? t('email_placeholder') : (language === 'en' ? '01XXXXXXXXX' : '০১XXXXXXXXX')}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  maxLength={loginMode === 'phone' ? 11 : undefined}
                  className="h-12"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{language === 'en' ? 'Password' : 'পাসওয়ার্ড'}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('password_placeholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 font-bold uppercase tracking-wide text-white shadow-lg transition-all hover:brightness-110 relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #013C10 0%, #0E461C 55%, #041408 100%)', boxShadow: '0 10px 24px -8px rgba(1,60,16,0.55), inset 0 1px 0 rgba(25,218,98,0.25)' }}
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? <Spinner size="sm" className="border-white" /> : t('login_button')}
              </Button>
            </form>

            {/* Back to MotoPotti */}
            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-slate-500 hover:text-emerald-700" data-testid="back-to-home-link">
                ← {language === 'en' ? 'Back to MotoPotti' : 'মোটোপটিতে ফিরে যান'}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
